// @core/stores/project/ProjectLabelStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { projectApi } from '@core/api';
import type {
  CreateLabelRequest,
  ProjectLabel,
  UpdateLabelRequest,
} from '@core/types/project';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ProjectLabelStore {
  // List state
  items: ProjectLabel[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: ProjectLabel | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, ProjectLabel>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): ProjectLabel | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: ProjectLabel | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create label
   */
  async CreateLabel(project_id: string, data: CreateLabelRequest): Promise<ProjectLabel> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.CreateLabel(project_id, data);
      const item = response.label;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete label
   */
  async DeleteLabel(project_id: string, label_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await projectApi.DeleteLabel(project_id, label_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== label_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === label_id) {
          this.currentItem = null;
        }

        this.removeFromCache(label_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * List labels
   */
  async ListLabels(project_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await projectApi.ListLabels(project_id, params);

      runInAction(() => {
        this.items = response.labels?.labels ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update label
   */
  async UpdateLabel(project_id: string, label_id: string, data: UpdateLabelRequest): Promise<ProjectLabel> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.UpdateLabel(project_id, label_id, data);
      const item = response.label;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const projectLabelStore = new ProjectLabelStore();
